import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  MapPin,
  CheckCircle2,
  ShieldCheck,
  Building2,
  GraduationCap,
  Dumbbell,
  Store,
  Hotel,
  Factory,
  Stethoscope,
  Warehouse,
} from "lucide-react";
import { slugify } from "@/data/states";
import { SF_NEIGHBORHOODS } from "@/data/sf-neighborhoods";
import { ogImageMeta } from "@/lib/og-image";
import { leafCanonical } from "@/lib/canonical";

const SF_FAQS = [
  {
    q: "Are vending machines really free for San Francisco, California businesses?",
    a: "Yes. Qualifying San Francisco, California businesses receive the vending machine, install, restocking, and ongoing maintenance at 100% no cost. We earn revenue from product sales — you provide the location.",
  },
  {
    q: "Which San Francisco neighborhoods do you cover?",
    a: "We serve every San Francisco neighborhood, including Downtown SF, Mission District, SoMa, North Beach, Chinatown, Fisherman's Wharf and beyond. Local California route operators handle delivery and service.",
  },
  {
    q: "How fast is installation in San Francisco, California?",
    a: "Most San Francisco (CA) locations are surveyed within 48 hours of applying and have a vending machine installed within 1–2 weeks of approval.",
  },
  {
    q: "What does my San Francisco, California business need to qualify?",
    a: "Typically 30+ daily employees or steady foot traffic, a standard 110V outlet, and floor space for a vending machine.",
  },
];

export const Route = createFileRoute("/states/california/san-francisco")({
  head: ({ match, matches }) => {
    const title = "Free Vending Machines San Francisco, CA";
    const desc =
      "Get a FREE vending machine for your San Francisco, California business. Full-service install, restocking & maintenance across all SF neighborhoods at zero cost.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        {
          property: "og:url",
          content: "https://vendingmachinefree.com/states/california/san-francisco",
        },
        { property: "og:type", content: "website" },
        ...ogImageMeta(),
      ],
      links: [
        ...leafCanonical("https://vendingmachinefree.com/states/california/san-francisco", { match, matches }),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: "Vending Machine Free.com — San Francisco, CA",
              areaServed: { "@type": "City", name: "San Francisco" },
              description: desc,
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: SF_FAQS.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]),
        },
      ],
    };
  },
  component: SanFranciscoPage,
});

const SERVE = [
  { icon: Building2, label: "Offices" },
  { icon: GraduationCap, label: "Schools" },
  { icon: Dumbbell, label: "Gyms" },
  { icon: Store, label: "Retail Stores" },
  { icon: Hotel, label: "Hotels" },
  { icon: Factory, label: "Factories" },
  { icon: Stethoscope, label: "Medical Centers" },
  { icon: Warehouse, label: "Warehouses" },
];

function SanFranciscoPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>{" "}
            /{" "}
            <Link to="/coverage" className="hover:text-safety">
              USA Map
            </Link>{" "}
            /{" "}
            <Link
              to="/states/$state"
              params={{ state: "california" }}
              className="hover:text-safety"
            >
              California
            </Link>{" "}
            / <span className="text-white">San Francisco</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> San Francisco, CA
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Machines in San Francisco, California
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">
            FREE vending machine placement, installation, restocking, insurance, and ongoing
            maintenance for San Francisco businesses. Offices, schools, gyms, retail stores, hotels,
            factories, medical centers, and warehouses all qualify for free placement across every
            SF neighborhood.
          </p>
          <a
            href="#apply"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Get a Vending Machine FREE for your business in San Francisco Now{" "}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Towns We Serve in San Francisco County
          </h2>
          <p className="mt-2 text-muted-foreground">
            Click any neighborhood for local coverage details. Hover to preview the town name.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {SF_NEIGHBORHOODS.map((town) => (
              <Link
                key={town}
                to="/states/california/san-francisco/$neighborhood"
                params={{ neighborhood: slugify(town) }}
                title={`Free vending machines in ${town}, San Francisco`}
                className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-safety hover:shadow-md transition"
              >
                <div className="h-10 w-10 rounded-md bg-steel/10 text-steel flex items-center justify-center group-hover:bg-safety/15 group-hover:text-safety transition">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">{town}</div>
                  <div className="text-xs text-muted-foreground">SF · Free placement</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase text-center">
            Who We Serve in San Francisco
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {SERVE.map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-lg bg-card border border-border p-5 text-center">
                <Icon className="h-8 w-8 text-safety mx-auto" />
                <div className="mt-2 font-semibold">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase text-center">
            Why SF Businesses Choose Us
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              {
                t: "Zero Cost",
                d: "No fees for San Francisco businesses. Machines, install, stocking, repairs — all free.",
              },
              {
                t: "Local SF Operators",
                d: "Route drivers based across San Francisco for fast service and same-day restocking.",
              },
              {
                t: "Modern Machines",
                d: "Touchscreen, tap-to-pay vending with Pepsi, Coca-Cola, snacks, and healthy options.",
              },
            ].map((x) => (
              <div key={x.t} className="rounded-lg bg-card p-6 border border-border">
                <ShieldCheck className="h-8 w-8 text-safety" />
                <h3 className="mt-3 font-display text-xl uppercase">{x.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-16 bg-secondary">
        <div className="container-x text-center">
          <h2 className="font-display text-3xl uppercase">
            Apply for a Free Vending Machine in San Francisco
          </h2>
          <p className="mt-3 text-muted-foreground">Local SF approval in as little as 48 hours.</p>
          <Link
            to="/"
            hash="contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Get a Vending Machine FREE for your business in San Francisco Now{" "}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-10 flex flex-wrap justify-center gap-5 text-sm">
            <Link
              to="/states/$state"
              params={{ state: "california" }}
              className="text-steel hover:text-safety font-semibold"
            >
              ← Back to California
            </Link>
            <Link to="/coverage" className="text-steel hover:text-safety font-semibold">
              USA Map
            </Link>
            <Link to="/" className="text-steel hover:text-safety font-semibold">
              Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
